import React, { useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { McpServer, McpServerTool, isMcpBinding } from '../../integrations/mcpServerRegistry';
import { APPROVAL_POLICIES, ApprovalPolicy, McpConnection, llmToolName } from '../servicesShared';

/** Mirrors mcp_client_service.is_read_only_tool: the discovery flag wins, else a read verb prefix. */
const READ_VERBS = /^(get|list|search|find|read|fetch|describe|query|lookup|show|count|check|retrieve|view)(_|$)/;
const isReadOnly = (tool: McpServerTool): boolean => {
    if (typeof tool.read_only === 'boolean') return tool.read_only;
    const snake = tool.name.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
    return READ_VERBS.test(snake);
};

const PolicySelect: React.FC<{ value: ApprovalPolicy | ''; onChange: (value: ApprovalPolicy | '') => void; allowInherit?: boolean }> = ({
    value,
    onChange,
    allowInherit,
}) => (
    <Select value={value || (allowInherit ? 'inherit' : 'auto')} onValueChange={(next) => onChange(next === 'inherit' ? '' : (next as ApprovalPolicy))}>
        <SelectTrigger className="w-[150px] h-8">
            <SelectValue />
        </SelectTrigger>
        <SelectContent>
            {allowInherit && <SelectItem value="inherit">Default</SelectItem>}
            {APPROVAL_POLICIES.map((policy) => (
                <SelectItem key={policy.value} value={policy.value}>
                    {policy.label}
                </SelectItem>
            ))}
        </SelectContent>
    </Select>
);

/**
 * Approval policy per granted write tool (tools.write_policy, keyed by the
 * LLM tool name "{slug}_{tool}" or "*" for the default). Read-only tools
 * never need approval and are not listed.
 */
export const WritePolicyTable: React.FC<{
    bindings: McpConnection[];
    servers: McpServer[];
    policy: Record<string, ApprovalPolicy>;
    onChange: (next: Record<string, ApprovalPolicy>) => void;
}> = ({ bindings, servers, policy, onChange }) => {
    const serversById = useMemo(() => new Map(servers.map((server) => [server.id, server])), [servers]);
    const rows = useMemo(() => {
        const out: { llmName: string; tool: string; server: string }[] = [];
        for (const binding of bindings) {
            if (!isMcpBinding(binding) || binding.enabled === false) continue;
            const server = serversById.get(binding.mcp_server_id);
            if (!server) continue;
            const enabled = binding.enabled_tools;
            for (const tool of server.tools || []) {
                if (Array.isArray(enabled) && !enabled.includes(tool.name)) continue;
                if (isReadOnly(tool)) continue;
                out.push({ llmName: llmToolName(binding.slug, tool.name), tool: tool.name, server: server.name });
            }
        }
        return out;
    }, [bindings, serversById]);

    const setPolicy = (key: string, value: ApprovalPolicy | '') => {
        const next = { ...policy };
        if (value) next[key] = value;
        else delete next[key];
        onChange(next);
    };

    const defaultPolicy = policy['*'] || 'auto';

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
                <span>Default for every write tool</span>
                <PolicySelect value={defaultPolicy} onChange={(value) => setPolicy('*', value || 'auto')} />
                <span className="text-xs text-muted-foreground">
                    {APPROVAL_POLICIES.find((item) => item.value === defaultPolicy)?.description}
                </span>
            </div>
            {rows.length > 0 ? (
                <div className="rounded-lg border overflow-hidden">
                    <Table className="table-fixed w-full">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Write tool</TableHead>
                                <TableHead style={{ width: 180 }}>Server</TableHead>
                                <TableHead style={{ width: 170 }}>Before each call</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.llmName}>
                                    <TableCell className="font-mono text-xs truncate" title={row.llmName}>
                                        {row.llmName}
                                    </TableCell>
                                    <TableCell className="text-sm truncate">{row.server}</TableCell>
                                    <TableCell>
                                        <PolicySelect allowInherit value={policy[row.llmName] || ''} onChange={(value) => setPolicy(row.llmName, value)} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            ) : (
                <p className="text-xs text-muted-foreground">
                    No write tools granted yet — only reads. Grant an MCP server above and its writes appear here.
                </p>
            )}
        </div>
    );
};

export default WritePolicyTable;
